/**
 * Co-browse controller (widget side) — reacts to the agent's ``cobrowse.control`` events.
 *
 * Two capture modes:
 * - **Embedded** (real sites): the host page's DOM lives OUTSIDE our iframe, so we ask the
 *   loader (via postMessage) to run the capture loop in the host context; it posts frames
 *   back and we relay them to the backend over the session-token REST endpoint.
 * - **Standalone** (dev harness / direct open): the widget page IS the top document — we
 *   capture it ourselves.
 *
 * A tiny subscribe() exposes the active flag so the widget UI can show a visible
 * "sharing your screen" banner with a Stop control (visitor consent/transparency).
 */

import { ApiError } from '@flozic/shared/api';
import { getWidgetApi } from '../config/loadWidgetConfig';
import { captureFrame, hashString } from './capture';
import { APP_SOURCE, isLoaderSource } from '../config/loaderProtocol';

const CAPTURE_MS = 2_000;

interface ControllerState {
  active: boolean;
  cid: string | null;
  sessionId: string | null;
}

const state: ControllerState = { active: false, cid: null, sessionId: null };
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let lastHash = 0;
let seq = 0;
let loaderListener: ((e: MessageEvent) => void) | null = null;
let cursor: { x: number; y: number } | null = null;

function onCursorMove(e: MouseEvent): void {
  cursor = { x: Math.round(e.pageX), y: Math.round(e.pageY) };
}

function notify(): void {
  listeners.forEach((l) => l());
}

export function subscribeCobrowse(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isCobrowsing(): boolean {
  return state.active;
}

function embedded(): boolean {
  try {
    return window.parent !== window;
  } catch {
    return true;
  }
}

async function ship(frame: {
  html?: string | null;
  scrollX: number;
  scrollY: number;
  viewportW: number;
  viewportH: number;
  pageUrl?: string;
}): Promise<void> {
  if (!state.active || !state.cid || !state.sessionId) return;
  try {
    await getWidgetApi().widget.cobrowseFrame(state.cid, state.sessionId, { seq, ...frame });
  } catch (err) {
    // 409 = the share is no longer active server-side (agent viewer gone / TTL
    // lapsed). Stop capturing and clear the visitor banner; other errors are
    // transient and the next tick retries.
    if (err instanceof ApiError && err.status === 409) stopCobrowse();
  }
}

/** Capture the widget's OWN document (standalone/dev-harness mode). */
function selfCaptureTick(): void {
  const captured = captureFrame(document, window);
  if (!captured) return;
  const withCursor = { ...captured, cursorX: cursor?.x ?? null, cursorY: cursor?.y ?? null };
  const h = hashString(captured.html);
  if (h !== lastHash) {
    lastHash = h;
    seq += 1;
    void ship(withCursor);
  } else {
    // Unchanged DOM — ship a light delta (scroll/cursor only); server reuses the html.
    void ship({ ...withCursor, html: null });
  }
}

/** Frames arriving from the loader (host-page capture in real embeds). */
function onLoaderMessage(e: MessageEvent): void {
  const data = e.data as { source?: string; type?: string; frame?: Record<string, unknown> };
  if (!data || !isLoaderSource(data.source) || data.type !== 'cobrowse-frame') return;
  const frame = data.frame as {
    html?: string;
    scrollX: number;
    scrollY: number;
    viewportW: number;
    viewportH: number;
    pageUrl?: string;
  };
  if (!frame) return;
  if (frame.html) {
    const h = hashString(frame.html);
    if (h !== lastHash) {
      lastHash = h;
      seq += 1;
    } else {
      frame.html = undefined; // unchanged — send as delta
    }
  }
  void ship(frame);
}

export function startCobrowse(cid: string, sessionId: string): void {
  if (state.active) return;
  state.active = true;
  state.cid = cid;
  state.sessionId = sessionId;
  lastHash = 0;
  seq = 0;
  if (embedded()) {
    loaderListener = onLoaderMessage;
    window.addEventListener('message', loaderListener);
    try {
      window.parent.postMessage({ source: APP_SOURCE, type: 'cobrowse-start' }, '*');
    } catch {
      /* no loader — nothing to capture */
    }
  } else {
    document.addEventListener('mousemove', onCursorMove);
    selfCaptureTick();
    timer = setInterval(selfCaptureTick, CAPTURE_MS);
  }
  notify();
}

export function stopCobrowse(): void {
  if (!state.active) return;
  state.active = false;
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  document.removeEventListener('mousemove', onCursorMove);
  if (loaderListener) {
    window.removeEventListener('message', loaderListener);
    loaderListener = null;
  }
  try {
    window.parent.postMessage({ source: APP_SOURCE, type: 'cobrowse-stop' }, '*');
  } catch {
    /* standalone */
  }
  notify();
}

/** Visitor-initiated stop (the banner's button): stop locally AND tell the backend,
 *  which clears the active flag and pushes `cobrowse.control stop` to the agent viewer. */
export function visitorStopCobrowse(): void {
  const { cid, sessionId } = state;
  stopCobrowse();
  if (cid && sessionId) {
    getWidgetApi()
      .widget.cobrowseStop(cid, sessionId)
      .catch(() => {
        /* best-effort — the 60s active TTL is the backstop */
      });
  }
}
